import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from 'helpers/axios';

// ################### CONTENT SAMPLES ###################

/**
 * Get all content samples for a brand
 */
export const getContentSamples = (brandId, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples${queryParams ? `?${queryParams}` : ''}`);
};

/**
 * Create a new content sample
 */
export const createContentSample = (brandId, sampleData) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples`, {
    content_sample: sampleData
  });
};

/**
 * Update a content sample
 */
export const updateContentSample = (brandId, sampleId, sampleData) => {
  return putRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples/${sampleId}`, {
    content_sample: sampleData
  });
};

/**
 * Delete a content sample
 */
export const deleteContentSample = (brandId, sampleId) => {
  return deleteRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples/${sampleId}`);
};

/**
 * Bulk delete content samples
 */
export const bulkDeleteSamples = (brandId, sampleIds) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples/bulk_delete`, {
    sample_ids: sampleIds
  });
};

/**
 * Toggle star/featured status on a sample
 */
export const toggleSampleStar = (brandId, sampleId) => {
  return putRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples/${sampleId}/toggle_star`);
};

/**
 * Import content from campaigns
 */
export const importFromCampaigns = (brandId, campaignIds) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples/import_campaigns`, {
    campaign_ids: campaignIds
  });
};

/**
 * Upload files as content samples
 */
export const uploadContentFiles = (brandId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files[]', file);
  });
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/content_samples/upload`, formData, {
    'Content-Type': 'multipart/form-data',
  });
};

// ################### VOICE DNA ANALYSIS ###################

/**
 * Get voice DNA analysis for a brand
 */
export const getVoiceDNA = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/voice_dna`);
};

/**
 * Trigger voice DNA analysis
 */
export const analyzeVoiceDNA = (brandId) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/voice_dna/analyze`);
};

/**
 * Get platform-specific voice analysis
 */
export const getPlatformVoice = (brandId, platform) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/voice_dna/platform/${platform}`);
};

// ################### INTERACTIVE TRAINING ###################

/**
 * Generate a training exercise
 */
export const generateExercise = (brandId, type = 'compare') => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/training/generate_exercise`, {
    exercise_type: type
  });
};

/**
 * Submit exercise response
 */
export const submitExerciseResponse = (brandId, response) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/training/submit_response`, {
    response
  });
};

/**
 * Get training progress and stats
 */
export const getTrainingProgress = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/training/progress`);
};

/**
 * Get learned preferences from training
 */
export const getLearnedPreferences = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/training/preferences`);
};

// ################### CONTENT GAPS & RECOMMENDATIONS ###################

/**
 * Get content gap analysis
 */
export const getContentGaps = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/content_gaps`);
};

/**
 * Get recommendations for improving content training
 */
export const getRecommendations = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/recommendations`);
};

// ################### CONTENT STRATEGY ###################

/**
 * Get content strategy settings
 */
export const getContentStrategy = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/content_strategy`);
};

/**
 * Update content strategy settings
 */
export const updateContentStrategy = (brandId, strategyData) => {
  return putRequest(`brand_profiles/${brandId}/brand_intelligence/content_strategy`, {
    content_strategy: strategyData
  });
};

/**
 * Generate AI-powered content strategy
 */
export const generateContentStrategy = (brandId) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/content_strategy/generate`);
};

// ################### TRAINING STATS ###################

/**
 * Get training status
 */
export const getTrainingStatus = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/training_status`);
};

/**
 * Get overall training stats
 */
export const getTrainingStats = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/training_stats`);
};

/**
 * Get AI readiness score
 */
export const getAIReadiness = (brandId) => {
  return getRequest(`brand_profiles/${brandId}/brand_intelligence/ai_readiness`);
};

// ################### SOCIAL CONTENT FETCH ###################

/**
 * Fetch more content from a connected social platform
 */
export const fetchSocialContent = (brandId, platform, count = 10) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/fetch_social_content`, {
    platform,
    count
  });
};

/**
 * Sync all connected social channels
 */
export const syncAllSocialContent = (brandId) => {
  return postRequest(`brand_profiles/${brandId}/brand_intelligence/sync_social_content`);
};

// Export as default object for easier imports
const contentTrainingService = {
  // Content Samples
  getContentSamples,
  createContentSample,
  updateContentSample,
  deleteContentSample,
  bulkDeleteSamples,
  toggleSampleStar,
  importFromCampaigns,
  uploadContentFiles,

  // Voice DNA
  getVoiceDNA,
  analyzeVoiceDNA,
  getPlatformVoice,

  // Interactive Training
  generateExercise,
  submitExerciseResponse,
  getTrainingProgress,
  getLearnedPreferences,

  // Gaps & Recommendations
  getContentGaps,
  getRecommendations,

  // Content Strategy
  getContentStrategy,
  updateContentStrategy,
  generateContentStrategy,

  // Training Stats
  getTrainingStatus,
  getTrainingStats,
  getAIReadiness,

  // Social Content
  fetchSocialContent,
  syncAllSocialContent,
};

export default contentTrainingService;
